import { Box, Avatar, Typography, MenuItem, IconButton, Menu, Tooltip, useTheme, Badge } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { useThemeMode } from '../../../shared/context/ThemeModeContext';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { splitUserName } from '../../../shared/utils/username';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../../../shared/constants/roles';
import { whatsappService } from '../../../shared/services/whatsapp/whatsappService';

const Header = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { mode, toggleMode } = useThemeMode();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Estado do WhatsApp
  const [whatsappConnected, setWhatsappConnected] = useState<boolean | null>(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [queueCount, setQueueCount] = useState(0);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const isAdmin = user?.role?.toLowerCase() === ROLES.ADMIN.toLowerCase();

  // Buscar status do WhatsApp e fila (apenas para admin)
  const fetchWhatsappStatus = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setWhatsappLoading(true);
      
      // Busca status e fila em paralelo
      const [statusResponse, queueResponse] = await Promise.all([
        whatsappService.getStatus(),
        whatsappService.getQueue().catch(() => null),
      ]);
      
      // "open" = conectado, qualquer outra coisa = desconectado
      setWhatsappConnected(statusResponse.data.state === 'open');
      
      // Atualiza contador da fila
      if (queueResponse?.data?.queue) {
        setQueueCount(queueResponse.data.queue.total);
      }
    } catch (err) {
      console.error('Erro ao buscar status do WhatsApp:', err);
      setWhatsappConnected(false);
    } finally {
      setWhatsappLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchWhatsappStatus();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchWhatsappStatus, 30000);
    
    return () => clearInterval(interval);
  }, [fetchWhatsappStatus]);

  // Gera tooltip baseado no status
  const getWhatsappTooltip = () => {
    if (whatsappConnected && queueCount > 0) {
      return `WhatsApp conectado - ${queueCount} mensagem(ns) pendente(s)`;
    }
    if (whatsappConnected) {
      return 'WhatsApp conectado';
    }
    if (queueCount > 0) {
      return `WhatsApp desconectado - ${queueCount} mensagem(ns) não enviada(s)`;
    }
    return 'WhatsApp desconectado - Clique para configurar';
  };

  const getWhatsappIcon = () => {
    if (whatsappConnected === null || whatsappLoading) return null;

    // Badge mostra contador de mensagens pendentes
    const badgeContent = queueCount > 0 ? queueCount : 0;

    return (
      <Tooltip title={getWhatsappTooltip()}>
        <IconButton
          onClick={() => navigate('/admin/whatsapp')}
          sx={{
            color: whatsappConnected ? '#25D366' : theme.palette.error.main,
            animation: (!whatsappConnected || queueCount > 0) ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.6 },
              '100%': { opacity: 1 },
            },
          }}
        >
          <Badge
            badgeContent={badgeContent}
            color={whatsappConnected ? 'warning' : 'error'}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                minWidth: 18,
                height: 18,
                fontWeight: 700,
              },
            }}
          >
            <WhatsAppIcon />
          </Badge>
        </IconButton>
      </Tooltip>
    );
  };

  const isManager = user?.role?.toLowerCase() === ROLES.ADMIN.toLowerCase();

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      bgcolor={theme.palette.background.paper}
      px={{ xs: 1, md: 3 }}
      py={2}
      boxShadow={1}
      sx={{ flexWrap: 'wrap', gap: 1 }}
    >
      <Box />
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {/* Ícone do WhatsApp com badge - apenas para admin */}
        {isAdmin && getWhatsappIcon()}

        <Tooltip title={mode === 'light' ? 'Modo Escuro' : 'Modo Claro'}>
          <IconButton onClick={toggleMode} color="primary">
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>
        <IconButton onClick={handleMenuOpen}>
          <Avatar sx={{ width: 36, height: 36 }} >
            {`${splitUserName(user?.name).firstName}${splitUserName(user?.name).lastName}`}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled>
            <Typography fontWeight={600}>{user?.name}</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate('/users/change-password');
              handleMenuClose();
            }}
          >
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            Alterar senha
          </MenuItem>

          <MenuItem onClick={() => { logout(); handleMenuClose(); }}>
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
            Sair
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Header;